import { aiRetouchPage } from "$lib/content/ai-retouch";
import { backgroundRemovePage } from "$lib/content/background-remove";
import { clippingPathPage } from "$lib/content/clipping-path";
import { colorCorrectionPage } from "$lib/content/color-correction";
import { ecommerceRetouchingPage } from "$lib/content/ecommerce-retouching";
import { editorialRetouchingPage } from "$lib/content/editorial-retouching";
import { ghostMannequinPage } from "$lib/content/ghost-mannequin";
import { jewelryRetouchingPage } from "$lib/content/jewelry-retouching";
import type { ServicePageData } from "$lib/types/service-detail";
import { mediaUrl } from "$lib/utils/media";
import { resolve } from "$app/paths";
import type { ResolvedPathname } from "$app/types";

function deepWrapMediaUrls<T>(obj: T): T {
  if (typeof obj === "string") {
    if (obj.startsWith("/images/") || obj.startsWith("/videos/")) {
      return mediaUrl(obj) as unknown as T;
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(deepWrapMediaUrls) as unknown as T;
  }
  if (obj && typeof obj === "object") {
    const res: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      res[k] = deepWrapMediaUrls(v);
    }
    return res as T;
  }
  return obj;
}

const rawServicePages: Record<string, ServicePageData> = {
  "background-remove": backgroundRemovePage,
  "clipping-path": clippingPathPage,
  "ghost-mannequin": ghostMannequinPage,
  "editorial-retouching": editorialRetouchingPage,
  "ecommerce-retouching": ecommerceRetouchingPage,
  "jewelry-retouching": jewelryRetouchingPage,
  "color-correction": colorCorrectionPage,
  "ai-retouch": aiRetouchPage,
};

export const servicePages: Record<string, ServicePageData> = deepWrapMediaUrls(rawServicePages);

export const implementedServiceSlugs = [...Object.keys(servicePages), "video-editing", "3d-modeling"];

const videoServiceSlugs = new Set([
  "commercial-editing",
  "ecommerce-video-editing",
  "color-grading",
  "social-cutdowns",
  "ai-video-generation",
  "video-editing",
]);
const threeDimensionalServiceSlugs = new Set([
  "product-modeling",
  "3d-product-modeling",
  "texturing-shading",
  "cgi-rendering",
  "turntables-motion",
  "3d-modeling",
]);

export function resolveServiceHref(slug: string): ResolvedPathname {
  if (servicePages[slug]) {
    return resolve("/services/[slug]", { slug });
  }
  if (slug === "video-editing") {
    return resolve("/services/video-editing");
  }
  if (videoServiceSlugs.has(slug)) {
    return `/services/video-editing#${slug}` as ResolvedPathname;
  }
  if (slug === "3d-modeling" || threeDimensionalServiceSlugs.has(slug)) {
    return resolve("/services/3d-modeling");
  }
  return resolve("/services#photo-editing");
}
